import { CommandDefinition, NeofetchContent, PortfolioContent } from '../types/terminal';

export const terminalPrompt = 'C:\\ANDREW>';

export const bootConfig = {
  biosLine: 'KELLOGG SYSTEMS BIOS v1.0',
  loadingLine: 'Loading andrewkellogg.com...',
  memoryLimitKb: 640,
};

export const portfolioContent: PortfolioContent = {
  name: 'Andrew Kellogg',
  title: 'Growth Engineer',
  location: 'Las Vegas, NV',
  about: [
    'I run Del Mar Advertising and work where strategy, creative, and technical execution meet.',
    'I build the systems behind customer acquisition, reporting, automation, and digital operations.',
    "I'm at my best when the solution does not exist yet and the gap has to be closed by building it.",
    'Execution first. Let the work and the results do the talking.',
  ],
  skills: [
    'Strategy, Positioning & Campaign Architecture',
    'Technical Marketing & Growth Systems',
    'Workflow Design, Automation & Integration',
    'Creative Direction, Graphics & Video Production',
    'Tracking, Reporting & Attribution',
    'AI Tooling, Prototyping & Problem Solving',
  ],
  stack: [
    'Strategy: campaign planning, offer development, channel direction',
    'Creative: Adobe Creative Suite, graphics, video, performance creative',
    'Web: React, Next.js, TypeScript, Node.js, APIs',
    'Analytics: GA4, Google Tag Manager, Search Console, attribution setup',
    'Media: Google Ads, Meta Ads, Microsoft Ads',
    'Automation: AI platforms, n8n, Zapier, custom workflows',
    'Systems: CMS, CRM, retail, and data integrations',
    'Ops: reporting dashboards, workflow design, process cleanup',
  ],
  projects: [
    {
      name: 'Green High Rollers',
      summary:
        "Turned a 4/20 promotion into a full acquisition system by connecting high-concept campaign creative, a custom landing experience, Alpine IQ loyalty flows, SMS verification, referrals, tiered rewards, and AI avatar generation across Green's Nevada locations.",
      url: 'https://highrollers.green',
      linkLabel: 'Live Site',
    },
  ],
  contact: [
    "Reach out if you're building something, fixing something, or trying to connect strategy with execution.",
    'Email: hello@andrewkellogg.com',
    'LinkedIn: https://www.linkedin.com/in/andrewkellogg/',
    'Agency: https://delmaradv.com',
    'Include a little context, what you are building, and where things are getting stuck.',
  ],
  socialLinks: [
    { label: 'Email', url: 'mailto:hello@andrewkellogg.com' },
    { label: 'LinkedIn', url: 'https://www.linkedin.com/in/andrewkellogg/' },
    { label: 'Instagram', url: 'https://www.instagram.com/andrewkellogg/' },
    { label: 'Del Mar Advertising', url: 'https://delmaradv.com' },
    { label: 'Website', url: 'https://andrewkellogg.com' },
  ],
};

export const commandDefinitions: CommandDefinition[] = [
  { name: 'help', description: 'List all available commands' },
  { name: 'neofetch', description: 'Show compact system profile' },
  { name: 'about', description: 'Show a short bio and role' },
  { name: 'skills', description: 'Show growth engineering capabilities' },
  { name: 'stack', description: 'Show tools and platforms' },
  { name: 'links', description: 'Show contact and external links' },
  { name: 'projects', description: 'Display project highlights' },
  { name: 'contact', description: 'Show contact information' },
  { name: 'whoami', description: 'Print current terminal user identity' },
  { name: 'cat', description: 'Print file contents (try: cat readme.txt)' },
  { name: 'theme', description: 'Set terminal theme: amber|green|blue|white' },
  { name: 'weather', description: 'Fetch current Las Vegas weather' },
  { name: 'history', description: 'Display command history' },
  { name: 'clear', description: 'Clear terminal output' },
  { name: 'ls', description: 'List terminal files/commands' },
  { name: 'play', description: 'Play a game (snake, pong, dino, asteroids, invaders)' },
  { name: 'matrix', description: 'See how deep the rabbit hole goes' },
  { name: 'sudo', description: 'Try elevated permissions' },
  { name: 'rm -rf /', description: 'Definitely safe command' },
];

export const primaryCommandButtons = [
  { label: 'About', command: 'about' },
  { label: 'Profile', command: 'neofetch' },
  { label: 'Skills', command: 'skills' },
  { label: 'Links', command: 'links' },
  { label: 'Contact', command: 'contact' },
  { label: 'Theme', command: 'theme' },
  { label: 'Games', command: 'play' },
];

export const bootHint = 'Type help or click a command below to get started';

export const readmeContent = [
  'Professional context: I run Del Mar Advertising.',
  'Personal context: I am a nerd who likes solving problems with technology.',
  'I like the point where strategy turns into systems, and systems turn into results.',
  'If the right solution does not exist yet, that is usually where I want to start.',
  'Execution first. Let the work speak.',
];

export const neofetchContent: NeofetchContent = {
  ascii: [
    '         _..._          ',
    "       .'    '. _       ",
    '      /   .-""-\\   _/ \\ ',
    '     .-|  /:. |    |  | ',
    "     |  \\ |:.  /.-'-./  ",
    "     | .-'-;:__. '  =/  ",
    "     .'=  *=|     _.='  ",
    '    /  _.  |    ;       ',
    "   ;-.-'|    \\   |      ",
    '  /  |  \\   _\\  _\\      ',
    "  \\__/'._;.  ='  ==\\    ",
    '           \\    \\   |   ',
    '           /   /  /     ',
    '          /-._/-._/     ',
    '          \\   `\\  \\     ',
    '           `-._/._/     ',
  ],
  stats: [
    { value: 'hello@andrewkellogg.com' },
    { value: '─────────────────────────' },
    { label: 'Name', value: 'Andrew Kellogg' },
    { label: 'Role', value: 'Growth Engineer' },
    { label: 'Base', value: 'Las Vegas, NV' },
    { label: 'Focus', value: 'Strategy, creative, technical execution' },
    { label: 'Builds', value: 'Campaign systems, workflow bridges, digital ops' },
    { label: 'Toolkit', value: 'AI, Adobe, React, analytics, automation' },
    { label: 'Mode', value: 'Execution first. Results speak.' },
    { label: 'Terminal', value: 'andrewkellogg.com v1.0' },
    { label: 'Theme', value: '{theme}' },
  ],
};

export const asciiHeader = {
  full: [
    ' █████╗ ███╗   ██╗██████╗ ██████╗ ███████╗██╗    ██╗',
    '██╔══██╗████╗  ██║██╔══██╗██╔══██╗██╔════╝██║    ██║',
    '███████║██╔██╗ ██║██║  ██║██████╔╝█████╗  ██║ █╗ ██║',
    '██╔══██║██║╚██╗██║██║  ██║██╔══██╗██╔══╝  ██║███╗██║',
    '██║  ██║██║ ╚████║██████╔╝██║  ██║███████╗╚███╔███╔╝',
    '╚═╝  ╚═╝╚═╝  ╚═══╝╚═════╝ ╚═╝  ╚═╝╚══════╝ ╚══╝╚══╝ ',
    '██╗  ██╗███████╗██╗     ██╗      ██████╗  ██████╗  ██████╗ ',
    '██║ ██╔╝██╔════╝██║     ██║     ██╔═══██╗██╔════╝ ██╔════╝ ',
    '█████╔╝ █████╗  ██║     ██║     ██║   ██║██║  ███╗██║  ███╗',
    '██╔═██╗ ██╔══╝  ██║     ██║     ██║   ██║██║   ██║██║   ██║',
    '██║  ██╗███████╗███████╗███████╗╚██████╔╝╚██████╔╝╚██████╔╝',
    '╚═╝  ╚═╝╚══════╝╚══════╝╚══════╝ ╚═════╝  ╚═════╝  ╚═════╝ ',
  ],
  compact: [
    '  ___   _   _  ____  ____  _____ _    _',
    ' / _ \\ | \\ | ||  _ \\|  _ \\| ____| |  | |',
    '| |_| ||  \\| || | | | |_) |  _| | |/\\| |',
    '|  _  || |\\  || |_| |  _ <| |___|  /\\  |',
    '|_| |_||_| \\_||____/|_| \\_\\_____|_/  \\_|',
    ' _  __ _____ _     _      ___   ____   ____',
    '| |/ /| ____| |   | |    / _ \\ / ___| / ___|',
    "| ' / |  _| | |   | |   | | | | |  _ | |  _",
    '| . \\ | |___| |___| |___| |_| | |_| || |_| |',
    '|_|\\_\\|_____|_____|_____|\\___/ \\____(_)____|',
  ],
  summary:
    'I build the systems behind creative development, customer acquisition, reporting, automation, and digital operations.',
  subtitle: 'Growth Engineer | Strategy x Creative x Systems',
  separator: '════════════════════════════════════',
};

export type TerminalThemeName = 'amber' | 'green' | 'blue' | 'white';

export const defaultTheme: TerminalThemeName = 'amber';

export const themeDefinitions: Record<
  TerminalThemeName,
  { fg: string; fgSoft: string; rgb: string; border: string; bg: string; bgGlow: string }
> = {
  amber: {
    fg: '#FFB000',
    fgSoft: '#FFCF70',
    rgb: '255, 176, 0',
    border: '#3D2D00',
    bg: '#0A0A0A',
    bgGlow: '#181818',
  },
  green: {
    fg: '#33FF00',
    fgSoft: '#8DFFA6',
    rgb: '51, 255, 0',
    border: '#1C4D11',
    bg: '#071006',
    bgGlow: '#0F1A0E',
  },
  blue: {
    fg: '#00AAFF',
    fgSoft: '#8CD6FF',
    rgb: '0, 170, 255',
    border: '#0A3C5B',
    bg: '#040B10',
    bgGlow: '#0C1620',
  },
  white: {
    fg: '#CCCCCC',
    fgSoft: '#F1F1F1',
    rgb: '204, 204, 204',
    border: '#4D4D4D',
    bg: '#0A0A0A',
    bgGlow: '#171717',
  },
};
